/**
 * Input Formatting Utilities
 * Provides consistent formatting for phone numbers, currency, dates, etc.
 */

/**
 * Phone number formatting for US/USVI numbers
 */
export const phoneFormatter = {
  /**
   * Format a phone number as user types
   * @param {string} value - Raw input value
   * @returns {string} Formatted phone number
   */
  format: (value) => {
    if (!value) return '';
    
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Handle different lengths
    if (digits.length <= 3) {
      return digits;
    }
    if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }
    if (digits.length <= 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    // With country code
    return `+${digits.slice(0, 1)} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 11)}`;
  },
  
  /**
   * Get raw digits from formatted phone number
   * @param {string} formatted - Formatted phone number
   * @returns {string} Raw digits
   */
  parse: (formatted) => {
    if (!formatted) return '';
    return formatted.replace(/\D/g, '');
  },
  
  /**
   * Validate phone number format
   * @param {string} value - Phone number to validate
   * @returns {boolean} Is valid
   */
  isValid: (value) => {
    const digits = phoneFormatter.parse(value);
    return digits.length >= 10 && digits.length <= 11;
  }
};

/**
 * Currency formatting for USD
 */
export const currencyFormatter = {
  /**
   * Format a number as USD currency
   * @param {number|string} value - Value to format
   * @param {Object} options - Formatting options
   * @returns {string} Formatted currency string
   */
  format: (value, { showSymbol = true, decimals = 2 } = {}) => {
    if (value === null || value === undefined || value === '') return '';
    
    const num = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : value;
    
    if (isNaN(num)) return '';
    
    const formatted = num.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
    
    return showSymbol ? `$${formatted}` : formatted;
  },
  
  /**
   * Format as user types (for input fields)
   * @param {string} value - Raw input value
   * @returns {string} Formatted value
   */
  formatInput: (value) => {
    if (!value) return '';
    
    // Remove non-numeric characters except decimal point
    let cleaned = value.replace(/[^0-9.]/g, '');
    
    // Ensure only one decimal point
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      cleaned = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Limit to 2 decimal places
    if (parts.length === 2 && parts[1].length > 2) {
      cleaned = parts[0] + '.' + parts[1].slice(0, 2);
    }
    
    return cleaned;
  },
  
  /**
   * Parse formatted currency back to number
   * @param {string} formatted - Formatted currency string
   * @returns {number} Numeric value
   */
  parse: (formatted) => {
    if (!formatted) return 0;
    const num = parseFloat(formatted.replace(/[^0-9.-]/g, ''));
    return isNaN(num) ? 0 : num;
  }
};

/**
 * Date formatting utilities with USVI timezone support
 * USVI uses Atlantic Standard Time (AST) year-round, no DST
 */
/**
 * Coerce any supported date-ish value to a Date.
 *
 * Firestore Timestamps are the common case in this app and do NOT survive
 * `new Date(value)` — it yields Invalid Date, which silently rendered as an
 * empty string (e.g. the admin Users "Joined" column). Duck-type on toDate()/
 * seconds rather than importing Timestamp, so plain {seconds,nanoseconds}
 * objects from serialized payloads work too.
 * @param {Date|string|number|{toDate?:Function,seconds?:number}} value
 * @returns {Date|null} null when the value is absent or unparseable
 */
const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value.toDate === 'function') {
    const d = value.toDate();
    return d instanceof Date && !isNaN(d.getTime()) ? d : null;
  }
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  if (typeof value._seconds === 'number') return new Date(value._seconds * 1000);
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

export const dateFormatter = {
  // USVI timezone
  TIMEZONE: 'America/Virgin',

  // Exposed so callers can normalize once instead of re-deriving this logic.
  toDate,

  /**
   * Format a date for display
   * @param {Date|string|number} date - Date to format
   * @param {Object} options - Formatting options
   * @returns {string} Formatted date string
   */
  format: (date, { 
    style = 'medium', 
    includeTime = false,
    timezone = 'America/Virgin'
  } = {}) => {
    const d = toDate(date);
    if (!d) return '';

    const dateOptions = {
      timeZone: timezone,
      ...(style === 'short' && { month: 'numeric', day: 'numeric', year: '2-digit' }),
      ...(style === 'medium' && { month: 'short', day: 'numeric', year: 'numeric' }),
      ...(style === 'long' && { month: 'long', day: 'numeric', year: 'numeric', weekday: 'long' }),
      ...(includeTime && { hour: 'numeric', minute: '2-digit', hour12: true })
    };
    
    return d.toLocaleDateString('en-US', dateOptions);
  },
  
  /**
   * Format date for input fields (YYYY-MM-DD)
   * @param {Date|string|number} date - Date to format
   * @returns {string} ISO date string
   */
  formatForInput: (date) => {
    const d = toDate(date);
    if (!d) return '';
    return d.toISOString().split('T')[0];
  },
  
  /**
   * Parse date input value
   * @param {string} value - Date string from input
   * @returns {Date|null} Parsed date
   */
  parseInput: (value) => {
    if (!value) return null;
    const d = new Date(value + 'T12:00:00');
    return isNaN(d.getTime()) ? null : d;
  },
  
  /**
   * Get relative time string (e.g., "2 hours ago")
   * @param {Date|string|number} date - Date to format
   * @returns {string} Relative time string
   */
  relative: (date) => {
    const d = toDate(date);
    if (!d) return '';

    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    const diffWeek = Math.floor(diffDay / 7);
    const diffMonth = Math.floor(diffDay / 30);
    const diffYear = Math.floor(diffDay / 365);
    
    if (diffSec < 60) return 'just now';
    if (diffMin < 60) return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`;
    if (diffHour < 24) return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`;
    if (diffDay < 7) return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
    if (diffWeek < 4) return `${diffWeek} week${diffWeek > 1 ? 's' : ''} ago`;
    if (diffMonth < 12) return `${diffMonth} month${diffMonth > 1 ? 's' : ''} ago`;
    return `${diffYear} year${diffYear > 1 ? 's' : ''} ago`;
  },
  
  /**
   * Format time only
   * @param {Date|string|number} date - Date to get time from
   * @returns {string} Formatted time
   */
  formatTime: (date, { timezone = 'America/Virgin' } = {}) => {
    const d = toDate(date);
    if (!d) return '';

    return d.toLocaleTimeString('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }
};

/**
 * Text truncation utility
 */
export const textFormatter = {
  /**
   * Truncate text with ellipsis
   * @param {string} text - Text to truncate
   * @param {number} maxLength - Maximum length
   * @param {string} suffix - Suffix to add (default: '...')
   * @returns {string} Truncated text
   */
  truncate: (text, maxLength, suffix = '...') => {
    if (!text || text.length <= maxLength) return text || '';
    return text.slice(0, maxLength - suffix.length) + suffix;
  },
  
  /**
   * Capitalize first letter
   * @param {string} text - Text to capitalize
   * @returns {string} Capitalized text
   */
  capitalize: (text) => {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  },
  
  /**
   * Convert to title case
   * @param {string} text - Text to convert
   * @returns {string} Title cased text
   */
  titleCase: (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  },
  
  /**
   * Slugify text for URLs
   * @param {string} text - Text to slugify
   * @returns {string} URL-safe slug
   */
  slugify: (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }
};

/**
 * Number formatting utilities
 */
export const numberFormatter = {
  /**
   * Format number with commas
   * @param {number} value - Number to format
   * @returns {string} Formatted number
   */
  format: (value) => {
    if (value === null || value === undefined) return '';
    return value.toLocaleString('en-US');
  },
  
  /**
   * Format as compact number (e.g., 1.2K, 3.5M)
   * @param {number} value - Number to format
   * @returns {string} Compact number string
   */
  compact: (value) => {
    if (value === null || value === undefined) return '';
    
    if (value >= 1000000) {
      return (value / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    }
    if (value >= 1000) {
      return (value / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    }
    return value.toString();
  },
  
  /**
   * Format as percentage
   * @param {number} value - Decimal value (0.75 = 75%)
   * @param {number} decimals - Decimal places
   * @returns {string} Percentage string
   */
  percentage: (value, decimals = 0) => {
    if (value === null || value === undefined) return '';
    return (value * 100).toFixed(decimals) + '%';
  },
  
  /**
   * Format as ordinal (1st, 2nd, 3rd, etc.)
   * @param {number} value - Number to format
   * @returns {string} Ordinal string
   */
  ordinal: (value) => {
    if (value === null || value === undefined) return '';
    
    const n = Math.abs(value);
    const suffix = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    
    return value + (suffix[(v - 20) % 10] || suffix[v] || suffix[0]);
  }
};

// Export all formatters as default object
const formatters = {
  phone: phoneFormatter,
  currency: currencyFormatter,
  date: dateFormatter,
  text: textFormatter,
  number: numberFormatter
};

export default formatters;
